// entry keyboard
export const EVENT_CODE_ESC = 'Escape';

/** =================== dialog button group =================== */
export const FOOT_DIRECTION = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
};

export const FOOT_FLEX_ALIGN = {
  BETWEEN: 'between',
  RIGHT: 'right',
  CENTER: 'center',
};

export const dialogButtonGroupProps = {
  /**
   * @description display direction
   */
  footerDirection: {
    type: String,
    default: FOOT_DIRECTION.HORIZONTAL,
    validator: (val) => Object.values(FOOT_DIRECTION).includes(val),
  },
  /**
   * @description footer align
   */
  footerAlign: {
    type: String,
    default: FOOT_FLEX_ALIGN.BETWEEN,
    validator: (val) => Object.values(FOOT_FLEX_ALIGN).includes(val),
  },
  /**
   * @description footer button options
   */
  footerOptions: {
    type: Object,
    default: () => ({
      clear: {
        label: 'Clear all',
      },
      cancel: {
        label: 'Cancel',
      },
      primary: {
        label: 'OK',
      },
    }),
  },
};

/** =================== dialog content props =================== */
export const dialogContentProps = {
  ...dialogButtonGroupProps,
  /**
   * @description dialog size
   */
  size: {
    type: String,
    default: 'md',
    validator: (val) => ['xs', 'sm', 'md', 'xl'].includes(val),
  },
  /**
   * @description dialog title font-size, if you are using mobile please change to 6
   */
  titleFont: {
    type: String,
    default: '5',
    validator: (val) => ['5', '6'].includes(val),
  },
  /**
   * @description dialog header title, you can set the title here
   */
  title: {
    type: String,
    default: '',
  },
  /**
   * @description whether to display back icon
   */
  isShowBack: {
    type: Boolean,
    default: false,
  },
  /**
   * @description is the header background transparent
   */
  headerTransparent: {
    type: Boolean,
    default: false,
  },
  /**
   * @description determine the number of lines for the title
   */
  titleLineClamp: {
    type: Number,
    default: null,
    validator(value) {
      return value >= 1 && value <= 3;
    },
  },
  /**
   * @description extra icon
   */
  extraIcon: {
    type: String,
    default: '',
  },
  /**
   * @description whether to show a close button
   */
  isShowClose: {
    type: Boolean,
    default: true,
  },
  /**
   * @description whether to show header
   */
  isShowHeader: {
    type: Boolean,
    default: true,
  },
  scrollTarget: null,
  /**
   * @description dialog maximum height
   */
  maxHeight: {
    type: [String, Number],
    default: null,
  },
  bodyClass: {
    type: String,
    default: null,
  },
  /**
   * @description whether to display footer shadow
   */
  isShowBoxShadow: {
    type: Boolean,
    default: true,
  },
  /**
   * @description whether to display footer
   */
  isShowFooter: {
    type: Boolean,
    default: true,
  },
  /**
   * @description this is a reserved prop
   */
  isFullScreen: {
    type: Boolean,
    default: false,
  },
};

/** =================== dialog main props =================== */
export const dialogProps = {
  ...dialogContentProps,
  /**
   * @description
   * which element the Dialog appends to
   * whether to append Dialog itself to body. A nested Dialog should have this attribute set to `body`
   */
  appendTo: {
    type: String,
    default: '',
  },
  /**
   * @description destroy elements in Dialog when closed
   */
  destroyOnClose: Boolean,
  /**
   * @description whether the Dialog can be closed by clicking the mask
   */
  closeOnClickModal: {
    type: Boolean,
    default: true,
  },
  /**
   * @description whether the Dialog can be closed by pressing ESC
   */
  closeOnPressEscape: {
    type: Boolean,
    default: true,
  },
  /**
   * @description visibility of Dialog
   */
  value: Boolean, // for v-model in vue2
  modelValue: Boolean, // for v-model in vue3
  /**
   * @description same as z-index in native CSS, z-order of dialog
   */
  zIndex: {
    type: Number,
  },
};

export const dialogEmits = [
  'open',
  'opened',
  'close',
  'closed',
  'input', // for v-model in vue2
  'update:modelValue', // for v-model in vue3
];

// dialog visibility controller
export const dialogController = {
  data: [],
  add({ doClose }) {
    const uuid = Symbol('KkDialog');
    this.data?.push({ uuid, doClose });
    return uuid;
  },
  remove(uuid) {
    const index = this.data?.findIndex((item) => item.uuid === uuid);

    if (index !== -1) {
      this.data?.splice(index, 1);
    }
  },
  getSize() {
    return this.data?.length || 0;
  },
  getLastDialog() {
    const { uuid, doClose } = this.data?.at(-1) || {};

    return {
      lastUuid: uuid,
      doClose,
    };
  },
};
